import SellIcon from '@mui/icons-material/Sell';
import {
    Box,
    CircularProgress,
    Collapse,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { FC, useMemo } from 'react';
import {
    capitalizeWords,
    formatReduction,
    getRatioGain,
    getRatioGainColor,
} from '../../../../../utils/format';
import { Report } from '../../../services/type';
import { Discipline } from '../../../services/enum';

interface Props {
    reports?: Array<Report>;
    currentReport: Report;
    isOpen: boolean;
    otherHorses: Array<{ nom: string; num: number }>;
}

const NP_ORDER = 999;

const CollapseDetail: FC<Props> = ({
    reports,
    isOpen,
    currentReport,
    otherHorses,
}) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const sortedReports = useMemo(() => {
        if (!reports?.length) return [];
        return [...reports].sort(
            (a, b) =>
                (Number(a.ordreArrivee) || NP_ORDER) -
                (Number(b.ordreArrivee) || NP_ORDER)
        );
    }, [reports]);

    const hasReports = sortedReports?.length > 0;

    const getTrotRow = (r: Report) => {
        const isCurrent = r.nom === currentReport.nom;
        const ordre = Number(r.ordreArrivee);
        const isInOtherRace = otherHorses.map((o) => o.nom).includes(r.nom);
        const hasBonusRecul = r.montantHorse / 100 < (r.recul || 0);
        const showRatio = currentReport.plafond != null;
        const ratio = getRatioGain(
            r.montantHorse / 100,
            r.plafond || undefined,
            r.recul || undefined
        );
        const gridColumns = showRatio
            ? "20px minmax(0, 1fr) 50px 22px 15px 15px 45px"
            : "20px minmax(0, 1fr) 50px 30px 15px 30px";

        return (
            <Box
                key={`${r.nom}-${r.numPMU}`}
                display="grid"
                gridTemplateColumns={gridColumns}
                alignItems="center"
                gap={1}
                px={isMobile ? 0 : 1}
                py={0.75}
                borderRadius={1}
                bgcolor={isCurrent ? 'secondary.light' : 'transparent'}
                color="primary.main"
                sx={{
                    transition: 'background-color 0.2s',
                    '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <Box display={'flex'} flexDirection={'row'}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {ordre || 'NP'}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        fontWeight={isInOtherRace ? 600 : 400}
                        color={'primary.light'}
                    >
                        {isInOtherRace
                            ? `(${otherHorses.find((o) => o.nom === r.nom)?.num}) ${capitalizeWords(r.nom)}`
                            : capitalizeWords(r.nom)}
                    </Typography>

                    {isInOtherRace && (
                        <SellIcon
                            sx={{
                                fontSize: 18,
                                color: 'primary.light',
                            }}
                        />
                    )}
                </Box>
                {hasBonusRecul ? (
                    <Typography
                        variant="caption"
                        sx={{
                            ml: 0.75,
                            px: 0.9,
                            py: 0.3,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'primary.light',
                            fontWeight: 600,
                            fontSize: 11,
                            letterSpacing: 0.3,
                        }}
                    >
                        {`-25m`}
                    </Typography>
                ) : (
                    <Box />
                )}
                {/* Âge */}
                <Typography
                    variant="caption"
                    whiteSpace="nowrap"
                    textAlign={r.age ? 'right' : 'center'}
                >
                    {`${r.age} ans`}
                </Typography>

                {/* Sexe */}
                <Typography
                    variant="caption"
                    whiteSpace="nowrap"
                    textAlign="center"
                    sx={{ opacity: r.sexe ? 1 : 0.5 }}
                >
                    {r.sexe?.[0] ?? '—'}
                </Typography>

                <Typography variant="caption" whiteSpace="nowrap" textAlign="right">
                    {r.reductionKilometrique
                        ? formatReduction(r.reductionKilometrique)
                        : '—'}
                </Typography>
                {showRatio && (
                    ratio != null ? (
                        <Typography
                            variant="caption"
                            textAlign="right"
                            whiteSpace="nowrap"
                            color={getRatioGainColor(ratio * 100, false)}
                        >
                            {`${Math.round(ratio * 100)}%`}
                        </Typography>
                    ) : (
                        <Box /> // garde l’alignement ligne
                    )
                )}
            </Box>
        );
    };

    const getOtherRow = (r: Report) => {
        const isCurrent = r.nom === currentReport.nom;
        const ordre = Number(r.ordreArrivee);
        const isInOtherRace = otherHorses.map((o) => o.nom).includes(r.nom);

        return (
            <Box
                key={`${r.nom}-${r.numPMU}`}
                display="grid"
                gridTemplateColumns="20px minmax(0, 1fr) 30px 15px 30px 40px"
                alignItems="center"
                gap={1}
                px={1}
                py={0.75}
                borderRadius={1}
                bgcolor={isCurrent ? 'secondary.light' : 'transparent'}
                color="primary.main"
                sx={{
                    transition: 'background-color 0.2s',
                    '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <Box display={'flex'} flexDirection={'row'}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {ordre || 'NP'}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        fontWeight={isInOtherRace ? 600 : 400}
                        color={'primary.light'}
                    >
                        {isInOtherRace
                            ? `(${otherHorses.find((o) => o.nom === r.nom)?.num}) ${capitalizeWords(r.nom)}`
                            : capitalizeWords(r.nom)}
                    </Typography>

                    {isInOtherRace && (
                        <SellIcon
                            sx={{
                                fontSize: 18,
                                color: 'primary.light',
                            }}
                        />
                    )}
                </Box>

                {/* Âge */}
                <Typography
                    variant="caption"
                    whiteSpace="nowrap"
                    textAlign={r.age ? 'right' : 'center'}
                >
                    {`${r.age} ans`}
                </Typography>

                {/* Sexe */}
                <Typography
                    variant="caption"
                    whiteSpace="nowrap"
                    textAlign="center"
                    sx={{ opacity: r.sexe ? 1 : 0.5 }}
                >
                    {r.sexe?.[0] ?? '—'}
                </Typography>

                <Typography variant="caption" whiteSpace="nowrap" textAlign="right">
                    {r.handicapValeur}
                </Typography>

                <Typography variant="caption" whiteSpace="nowrap" textAlign="right">
                    {`${r.handicapPoids / 10}kg`}
                </Typography>
            </Box>
        );
    };

    return (
        <Collapse in={isOpen} sx={{ width: '100%' }}>
            <Box px={isMobile ? 0.5 : 2} py={1}>
                {/* ===== INFOS COURSE ===== */}
                <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    {currentReport.montantHorse >= 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                fontWeight: 600,
                                fontSize: 11,
                            }}
                        >
                            {`Montant: ${(currentReport.montantHorse / 100000).toFixed(1)} k€`}
                        </Typography>
                    )}
                    {currentReport.plafond && (
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                fontWeight: 600,
                                fontSize: 11,
                            }}
                        >
                            {`Plafond: ${(currentReport.plafond / 1000).toLocaleString()} k€`}
                        </Typography>
                    )}

                    {currentReport.recul && (
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                bgcolor: 'secondary.light',
                                color: 'secondary.contrastText',
                                fontWeight: 600,
                                fontSize: 11,
                            }}
                        >
                            {`Recul: ${(currentReport.recul / 1000).toLocaleString()} k€`}
                        </Typography>
                    )}
                </Box>

                {!hasReports && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={20} color="secondary" />
                    </Box>
                )}

                {hasReports &&
                    sortedReports.map((r) =>
                        currentReport.discipline === Discipline.ATTELE ||
                            currentReport.discipline === Discipline.MONTE
                            ? getTrotRow(r)
                            : getOtherRow(r)
                    )}
            </Box>
        </Collapse>
    );
};

export default CollapseDetail;
